import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import {
  MagnifyingGlass,
  Stethoscope,
  User as UserIcon,
  Buildings,
  ClipboardText,
  CircleNotch,
  X,
} from '@phosphor-icons/react';

const GROUP_ICON = {
  professionisti: Stethoscope,
  clienti: UserIcon,
  centri: Buildings,
  richieste: ClipboardText,
};

const PLACEHOLDER = {
  admin: 'Cerca clienti, professionisti, richieste…',
  professional: 'Cerca nelle tue richieste…',
  user: 'Cerca professionisti o le tue richieste…',
};

const GlobalSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'user';
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const runSearch = useCallback(async (term) => {
    if (term.trim().length < 2) { setGroups(null); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/api/search/quick?q=${encodeURIComponent(term.trim())}`);
      setGroups(r.data?.groups || []);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const itemRoute = (groupKey) => {
    const term = encodeURIComponent(q.trim());
    if (groupKey === 'professionisti') return (id) => `/professional/${id}`;
    if (groupKey === 'clienti' || groupKey === 'centri') return () => `/admin/crm?q=${term}`;
    if (groupKey === 'richieste') {
      if (role === 'admin') return () => `/admin/crm?q=${term}`;
      if (role === 'professional') return () => '/professional/richieste';
      return () => '/dashboard/richieste';
    }
    return () => '/';
  };

  const handleSelect = (groupKey, item) => {
    const route = itemRoute(groupKey)(item.id);
    setOpen(false);
    setQ('');
    setGroups(null);
    navigate(route);
  };

  const hasResults = groups && groups.some((g) => g.items && g.items.length > 0);

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="flex items-center gap-2 h-10 px-3 rounded-full bg-[#F5FAFF] border border-[#E3F2FD] focus-within:border-primary/40 focus-within:bg-white transition-colors">
        <MagnifyingGlass size={18} className="text-slate-400 shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={PLACEHOLDER[role]}
          className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-slate-400 min-w-0"
          data-testid="global-search-input"
        />
        {q && (
          <button
            onClick={() => { setQ(''); setGroups(null); }}
            className="text-slate-400 hover:text-slate-600 shrink-0"
            data-testid="global-search-clear"
            aria-label="Pulisci ricerca"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white rounded-2xl border border-[#E3F2FD] shadow-[0_8px_28px_rgba(21,101,192,0.12)] z-50 overflow-hidden max-h-[70vh] overflow-y-auto"
          data-testid="global-search-dropdown"
        >
          {loading && (
            <div className="flex items-center gap-2 px-4 py-4 text-sm text-slate-500" data-testid="global-search-loading">
              <CircleNotch size={16} className="animate-spin" /> Ricerca in corso…
            </div>
          )}

          {!loading && !hasResults && (
            <div className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="global-search-empty">
              Nessun risultato per “{q.trim()}”
            </div>
          )}

          {!loading && hasResults && groups.map((group) => {
            if (!group.items || group.items.length === 0) return null;
            const Icon = GROUP_ICON[group.key] || ClipboardText;
            return (
              <div key={group.key} className="py-1">
                <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                  {group.label}
                </div>
                {group.items.map((item) => (
                  <button
                    key={`${group.key}-${item.id}`}
                    onClick={() => handleSelect(group.key, item)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#F5FAFF] transition-colors"
                    data-testid={`global-search-result-${group.key}-${item.id}`}
                  >
                    <span className="w-8 h-8 rounded-lg bg-secondary text-primary flex items-center justify-center shrink-0">
                      <Icon size={16} weight="duotone" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground truncate">{item.title}</span>
                      {item.subtitle && (
                        <span className="block text-xs text-slate-500 truncate">{item.subtitle}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
